import { Alert, Box, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CustomSwitch } from '../atoms/CustomSwitch'
import { callAxios } from '../../hooks/useAxios'
import { useApp } from '../../hooks/useApp'
import { Location } from '../../types/models'

export type MultioptProxyToggleProps = {
  location: Location
}

/**
 * Installer-level toggle for using the server as the transport for multiopt
 * messages between the location's Enion devices. Meant for locations where the
 * devices sit behind the same meter but in different LAN networks, so the
 * normal UDP broadcast path does not reach between them.
 */
export function MultioptProxyToggle({ location }: MultioptProxyToggleProps): React.ReactElement {
  const app = useApp()
  const { t } = useTranslation()
  const checked = !!location.multiopt_server_proxy

  const toggle = async () => {
    const res = await callAxios({ method: 'PUT', url: `/locations/${location.id}` }, { multiopt_server_proxy: !checked })
    if (res.success) {
      app.setLocations(app.locations.map(loc => (loc.id === location.id ? { ...loc, multiopt_server_proxy: !checked } : loc)))
    } else {
      app.addError(t('Saving the setting failed.'))
    }
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Box>
        <CustomSwitch data-cy={`switch-Multiopt Server Proxy-${location.id}`} checked={checked} onClick={toggle} sx={{ ml: '1rem' }}/>
        <Typography component="span" sx={{ ml: 2 }}>{t('Use server as proxy for multi-Enion communications')}</Typography>
      </Box>
      {checked &&
        <Alert severity="warning" sx={{ mt: 1, ml: 2 }} data-cy="alert-Multiopt Server Proxy">
          {t('Are you sure? Use this mode only when the devices are at the same site, behind the same meter, but in different LAN networks (e.g., separate internet connections).')}
        </Alert>}
    </Box>
  )
}
